// React Components
import React, { useEffect, useState, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

// Utilities & Helpers
import Helpers from '@Apis/helpers';
import { toISTDate } from '@Apis/constants';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';

// Actions
import STActions from '@Actions/stActions';
import AttachmentActions from '@Actions/attachmentActions';
import AnalyticsActions from '@Actions/application/analyticsActions';
import GrnActions from '@Actions/grnActions';

// Common Components
import ViewLoadingSkull from '../../../Common/ViewLoadingSkull';
import ViewDocRightSection from '../../../Common/ViewDocRightSection';
import ViewDocumentFooterDetails from '../../../Common/ViewDocFooterLeftSection';
import PRZDrawer from '../../../Common/UI/PRZDrawer';

// GRN & Related
import ViewSTGRN from '@Components/Purchase/GoodsReceiving/GoodsReceivingHome/ViewSTGRN';
import GrnForm from '../GrnForm';

// custom components
import ViewStHeaders from './ViewStHeaders';
import ViewStDetails from './ViewStDetails';
import StockTransferLines from './StockTransferLines';
import ViewDeliveryChallan from '@Components/Inventory/DeliveryChallan/ViewDeliveryChallan';
import DeliveryChallanForm from '../../DeliveryChallan/DeliveryChallanForm';

// Styles
import './style.scss';

// Utilities (Local File)
import {
  initialState,
  getLinkedDocumentInfo,
  getStockTransferType,
  getTenantIdList,
  handleFileChangeHelper
} from './helper';

const ViewStockTransfer = (props) => {
  const {
    selectedSt, selectedSTId, user, match, history, isQuickView,
    getStById, getStByIdSuccess, updateStStatusLoading, deleteStLoading,
    updateStStatus, deleteSt, createStShortClose, createStShortCloseLoading,
    updateSTWorkflowStep, updateSTWorkflowStepLoading, getGRN,
    getAttachmentById, getAttachmentByIdLoading, updateAttachment, updateAttachmentLoading,
    selectedAttachment, downloadDocument, getStByIdLoading
  } = props;

  const { stockTransferId } = match.params;

  const [state, setState] = useState(initialState);
  const {
    showNewGrnForm,
    showDCDrawer,
    openGRNModalView,
    openDCModalView,
    selectedGRN,
    selectedDC,
    fileList,
    cfStockTransferLine,
    visibleColumns,
    isUserReady,
    isUserReadyForPrefillData,
    isGetAttachment,
    call
  } = state;

  const updateState = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const tenantIdList = getTenantIdList(user);

  useEffect(() => {
    if (user?.tenant_info?.tenant_id && !isUserReady && (stockTransferId || selectedSt?.stock_transfer_id)) {
      getStById(tenantIdList, stockTransferId || selectedSt?.stock_transfer_id);
      setState(prev => ({ ...prev, isUserReady: true }));
    }
  }, [user, stockTransferId, isUserReady]);

  useEffect(() => {
    if (selectedSt && !isUserReadyForPrefillData && isUserReady) {
      const lineCf = selectedSt?.stock_transfer_lines?.[0]?.st_line_custom_fields;
      setState(prev => ({
        ...prev,
        fileList: selectedSt?.attachments,
        cfStockTransferLine: lineCf,
        visibleColumns: CustomFieldHelpers.updateVisibleColumns(lineCf, prev.visibleColumns),
        isUserReadyForPrefillData: true
      }));
    }
  }, [selectedSt, isUserReadyForPrefillData, isUserReady]);

  useEffect(() => {
    if (selectedAttachment && isGetAttachment && !getAttachmentByIdLoading && !updateAttachmentLoading) {
      setState(prev => ({
        ...prev,
        fileList: selectedAttachment,
        isGetAttachment: false
      }));
    }
  }, [selectedAttachment, isGetAttachment, getAttachmentByIdLoading, updateAttachmentLoading]);

  useEffect(() => {
    if (selectedSTId) {
      getStById(tenantIdList, selectedSTId);
    } else if (selectedSt?.stock_transfer_id || stockTransferId) {
      getStById(tenantIdList, (selectedSt?.stock_transfer_id || stockTransferId));
    }
    return () => {
      getStByIdSuccess(null);
    };
  }, [selectedSTId, tenantIdList, call]);

  const handleFileChange = (fileListData) => {
    handleFileChangeHelper({
      fileListData,
      selectedSt,
      updateAttachment,
      getAttachmentById,
      setState,
    });
  };
  const linkedDocumentsInfo = getLinkedDocumentInfo({
    selectedSt,
    user,
    isQuickView,
    openGRNModalView,
    openDCModalView,
    setState
  });

  const fixedMenuBar = user?.side_menu_bar_type === 'FIXED';
  const createGRNFromInternalST = user?.tenant_info?.inventory_config?.sub_modules?.stock_transfer?.settings?.create_grn_incase_of_internal_stock_transfers;
  const conditionalGRN = createGRNFromInternalST ? true : selectedSt?.source_department_info?.tenant_id !== selectedSt?.destination_department_info?.tenant_id;
  const stockTransferType = getStockTransferType(selectedSt, user);

  return (
    <div className={`view-document__wrapper ${isQuickView ? '' : 'view-document__wrapper-page'}`}>
      {getStByIdLoading ? (
        <ViewLoadingSkull isQuickView={isQuickView} fixedMenuBar={fixedMenuBar} />
      ) : (
        <div className="ant-row">
          <div className={`${fixedMenuBar ? 'ant-col-md-16' : 'ant-col-md-17'} ant-col-xs-24`}>
            <div className={`view-left__wrapper ${isQuickView ? '' : 'is-page-view'}`}>
              <ViewStHeaders
                selectedSt={selectedSt}
                user={user}
                isQuickView={isQuickView}
                updateStStatusLoading={updateStStatusLoading}
                deleteStLoading={deleteStLoading}
                conditionalGRN={conditionalGRN}
                stockTransferId={stockTransferId}
                selectedSTId={selectedSTId}
                history={history}
                callback={() => updateState('call', !call)}
                updateStStatus={updateStStatus}
                deleteSt={deleteSt}
                createStShortClose={createStShortClose}
                createStShortCloseLoading={createStShortCloseLoading}
                downloadDocument={downloadDocument}
                setShowDCDrawer={(val) => updateState('showDCDrawer', val)}
                setShowNewGrnForm={(val) => updateState('showNewGrnForm', val)}
              />
              <ViewStDetails selectedSt={selectedSt} toISTDate={toISTDate} />
              <br />
              <StockTransferLines
                selectedSt={selectedSt}
                cfStockTransferLine={cfStockTransferLine}
                visibleColumns={visibleColumns}
              />
              <div className="view-document__totals-wrapper">
                <ViewDocumentFooterDetails
                  remark={selectedSt?.remark?.trim()}
                  fileList={fileList}
                  termsAndConditions={selectedSt?.t_and_c}
                  disableAttachment={getAttachmentByIdLoading || updateAttachmentLoading}
                  onFileChange={handleFileChange}
                  canUpdateAttachment={Helpers.getPermission(
                    Helpers.permissionEntities.STOCK_TRANSFER,
                    Helpers.permissionTypes.UPDATE,
                    user
                  )}
                />
              </div>
            </div>
          </div>
          <ViewDocRightSection
            user={user}
            isQuickView={isQuickView}
            entityType="STOCK_TRANSFER"
            entityId={selectedSt?.stock_transfer_id}
            workflowTimelineProps={{
              isEnabled: selectedSt?.status !== 'DRAFT',
              workflowSteps: selectedSt?.workflow_steps || {},
              workflowStepId: selectedSt?.workflow_step_id,
              loading: updateSTWorkflowStepLoading,
              updateStep: updateSTWorkflowStep,
              callback: () =>
                updateState('call', !call)
            }}
            activityLogProps={{
              isEnabled: selectedSt,
              entityName: 'Stock Transfer',
              entityType: 'stock_transfer',
            }}
            linkedDocumentsInfo={linkedDocumentsInfo}
          />
        </div>
      )}
      <PRZDrawer
        open={showNewGrnForm}
        onClose={() => updateState('showNewGrnForm', false)}
        title="Create Goods Receiving Note"
        width={1250}
        headerWidth={1200}
      >
        <GrnForm
          selectedSt={selectedSt}
          callback={() => {
            updateState('showNewGrnForm', false);
            updateState('call', !call);
            getGRN(
              tenantIdList,
              null,
              selectedSt?.stock_transfer_id || stockTransferId,
              'INVENTORY_TRANSFER',
              '',
              30,
              1,
              null
            );
          }}
          changeRoute
          TenantDepartmentId={selectedSt?.destination_tenant_department_id}
          stockTransferType={stockTransferType}
        />
      </PRZDrawer>
      <PRZDrawer
        open={showDCDrawer}
        onClose={() => updateState('showDCDrawer', false)}
        title="Create Delivery Challan"
        width={1200}
        headerWidth={1150}
      >
        <DeliveryChallanForm
          selectedSt={selectedSt}
          callback={() => {
            updateState('showDCDrawer', false);
            updateState('call', !call);
          }}
          documentType="STOCK_TRANSFER"
        />
      </PRZDrawer>
      <PRZDrawer
        open={openGRNModalView}
        onClose={() => {
          getGRN(
            tenantIdList,
            null,
            selectedSt?.stock_transfer_id || stockTransferId,
            'INVENTORY_TRANSFER',
            '',
            30,
            1,
            null
          );
          updateState('openGRNModalView', false);
        }}
        title="View Goods Receiving Note"
        width={1080}
        headerWidth={1030}
      >
        <ViewSTGRN
          destinationTenantId={selectedSt?.destination_department_info?.tenant_id}
          selectedGrnId={selectedGRN}
          selectedSt={selectedSt}
          callback={() => {
            updateState('openGRNModalView', false);
            updateState('call', !call);
            getGRN(
              Helpers.getTenantEntityPermission(
                user?.user_tenants,
                Helpers.permissionEntities.GOOD_RECEIVING,
                Helpers.permissionTypes.READ
              )?.join(','),
              null,
              selectedSt?.stock_transfer_id || stockTransferId,
              'INVENTORY_TRANSFER',
              '',
              30,
              1,
              null
            );
          }}
          fixedRoute
          isQuickView
        />
      </PRZDrawer>
      <PRZDrawer
        open={openDCModalView}
        onClose={() => updateState('openDCModalView', false)}
        title="View Delivery Challan"
        width={920}
        headerWidth={875}
      >
        <ViewDeliveryChallan
          selectedDcSourceTenant={selectedSt?.source_department_info}
          selectedDCId={selectedDC}
          callback={() => {
            updateState('openDCModalView', false);
            updateState('call', !call);
          }}
          selectedDocType="CHL_ST"
          isQuickView
        />
      </PRZDrawer>
    </div>
  );

};

const mapStateToProps = ({ UserReducers, STReducers, AttachmentReducers }) => ({
  user: UserReducers.user,
  getStByIdLoading: STReducers.getStByIdLoading,
  selectedSt: STReducers.selectedSt,
  updateStStatusLoading: STReducers.updateStStatusLoading,
  deleteStLoading: STReducers.deleteStLoading,
  selectedAttachment: AttachmentReducers.selectedAttachment,
  getAttachmentByIdLoading: AttachmentReducers.getAttachmentByIdLoading,
  updateAttachmentLoading: AttachmentReducers.updateAttachmentLoading,
  createStShortCloseLoading: STReducers.createStShortCloseLoading,
  updateSTWorkflowStepLoading: STReducers.updateSTWorkflowStepLoading,
});

const mapDispatchToProps = (dispatch) => ({
  updateStStatus: (payload, cb) => dispatch(STActions.updateStStatus(payload, cb)),
  deleteSt: (payload, cb) => dispatch(STActions.deleteSt(payload, cb)),
  getStById: (tenantId, stId) => dispatch(STActions.getStById(tenantId, stId)),
  getStByIdSuccess: (st) => dispatch(STActions.getStByIdSuccess(st)),
  getGRN: (...args) => dispatch(GrnActions.getGRN(...args)),
  getAttachmentById: (id, name, cb) => dispatch(AttachmentActions.getAttachmentById(id, name, cb)),
  updateAttachment: (payload, cb) => dispatch(AttachmentActions.updateAttachment(payload, cb)),
  createStShortClose: (payload, cb) => dispatch(STActions.createStShortClose(payload, cb)),
  updateSTWorkflowStep: (payload, cb) => dispatch(STActions.updateSTWorkflowStep(payload, cb)),
  downloadDocument: (payload, doc) => dispatch(AnalyticsActions.downloadDocument(payload, doc)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ViewStockTransfer));
